/**
 * Device verification, composed for the real app — port, key vault, and the one
 * surface a passphrase can actually be typed into.
 *
 * `browserDeviceVerification()` has existed since P21 and nothing called it, so
 * every build reached `UNAVAILABLE_DEVICE_VERIFICATION` through
 * `PrivacySessionProvider`'s honest fallback: availability `unavailable`,
 * `verify()` false, and therefore Journal Lock permanently un-turn-on-able
 * (F-JP-06 never fails open, so an unavailable verifier can only ever refuse).
 * This module is what makes the answer depend on the device instead.
 *
 * Two halves, and they only work as a pair. `DeviceVerificationPort.verify()`
 * returns a promise and cannot render a text field, so the passphrase enrolment
 * is constructed with a `PassphrasePrompt` that resolves from
 * {@link PassphraseChallengeBroker}, and {@link PassphraseChallengeSurface}
 * renders whatever the broker has outstanding. Composing the port without
 * mounting the surface would produce a `verify()` that never settles — which is
 * strictly worse than the unavailable port it replaced, because the privacy
 * session would sit in `authenticating` forever.
 *
 * The WebAuthn member is first in the composite (`browserDeviceVerification`
 * orders them), so the passphrase field appears only where there is no platform
 * authenticator and a passphrase has been enrolled. That is why the surface's
 * hint names the passphrase rather than "this device": by the time it is on
 * screen, the passphrase *is* the verifier.
 */

import type { ReactNode } from "react";
import { useCallback, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { JOURNAL_AUTH_REASONS, JOURNAL_PRIVACY_COPY } from "@ff/domain";
import { Button, Sheet, Text } from "@ff/ui";

import type { PassphraseChallenge } from "../privacy/passphraseChallenge";
import { PassphraseChallengeBroker } from "../privacy/passphraseChallenge";
import type { PassphraseKeyVault } from "../platform/auth";
import { browserDeviceVerification } from "../platform/auth";
import type { PortBundle } from "./ports";

/**
 * Web-only copy (§9.2).
 *
 * The native product never showed a passphrase field: iOS raised a system
 * prompt whose chrome and wording Apple owned, and the spec's F-JP-15 copy
 * describes the *curtain* behind it, not the prompt itself. So there is no
 * sentence to translate here and these are stated as a substitution, in one
 * place, rather than invented at the point of render.
 *
 * The two actions are **not** new copy: they are `JOURNAL_PRIVACY_COPY.unlock`
 * and `.notNow`, the same words the curtain uses, because this dialog is the
 * step the curtain's Unlock leads to and a second vocabulary for the same
 * choice is how a user learns that "Not now" means something different here.
 */
export const PASSPHRASE_CHALLENGE_COPY = Object.freeze({
  title: "Journal passphrase",
  field: "Journal passphrase",
  hint: "Fighting Form verifies you with the passphrase set for this browser profile.",
});

export const PASSPHRASE_CHALLENGE_TEST_IDS = Object.freeze({
  surface: "journal.privacy.passphrase",
  field: "journal.privacy.passphrase.field",
  submit: "journal.privacy.passphrase.submit",
  cancel: "journal.privacy.passphrase.cancel",
});

export interface DeviceVerificationComposition {
  /** The incoming bundle, with `deviceVerification` filled in when it was absent. */
  readonly ports: PortBundle;
  /** Rendered by {@link PassphraseChallengeSurface}. */
  readonly challenges: PassphraseChallengeBroker;
  /**
   * What the privacy session runs on `foregroundLost`. Absent when this
   * composition holds no key material — a caller that supplied its own
   * verification port has nothing here to drop, and a no-op passed anyway would
   * claim a key was released that never existed.
   */
  readonly dropKeyMaterial?: () => void;
  /**
   * The passphrase enrolment behind the port, when this composition built one.
   *
   * Published for the reason the adapter states: the key it holds has a
   * lifetime the port does not describe, and **enrolling is not a verification**
   * — a screen that sets the passphrase for the first time has to reach the
   * vault, and there is no `DeviceVerificationPort` method that could carry it.
   * The port stays the single interface for *asking whether the owner was
   * verified*; nothing here is a second one.
   */
  readonly passphrase?: PassphraseKeyVault;
}

/**
 * Fills in the verification port, or leaves a supplied one alone.
 *
 * The caller's port wins wholesale. A test that installs a fake verifier is
 * stating what the device answers, and composing a browser one beside it would
 * mean two authorities for "did the platform verify the owner" with only one of
 * them wired to the prompt.
 *
 * The key-value store is the app's own preference port: the credential id, the
 * salt and the check record are profile-wide and must be visible to every tab,
 * which is exactly what `KeyValuePort` is and what per-tab scene storage is not.
 */
export function composeDeviceVerification(ports: PortBundle): DeviceVerificationComposition {
  const challenges = new PassphraseChallengeBroker();

  if (ports.deviceVerification !== undefined) {
    return { ports, challenges };
  }

  const verification = browserDeviceVerification({
    store: ports.preferences,
    prompt: challenges.prompt,
  });

  return {
    ports: { ...ports, deviceVerification: verification.port },
    challenges,
    passphrase: verification.passphrase,
    dropKeyMaterial: () => {
      // Both, and in this order. Dropping the derived key without abandoning an
      // outstanding challenge would leave a passphrase field open across a
      // foreground loss, to be answered into a session that has already revoked
      // its lease — and `abandon()` resolves that `verify()` as a refusal, which
      // is the fail-closed outcome.
      verification.passphrase.drop();
      challenges.abandon();
    },
  };
}

/**
 * Built once per bundle identity, and never rebuilt on an unrelated render: the
 * broker holds the pending challenge, and a new one mid-prompt would strand the
 * `verify()` waiting on the old.
 */
export function useDeviceVerification(ports: PortBundle): DeviceVerificationComposition {
  return useMemo(() => composeDeviceVerification(ports), [ports]);
}

export interface PassphraseChallengeSurfaceProps {
  readonly broker: PassphraseChallengeBroker;
}

/**
 * The field the broker has been waiting for.
 *
 * Mounted above the shell rather than inside the Journal destination, because a
 * challenge can be raised from anywhere the lock is: Settings' Journal privacy
 * pane turning the lock off, the Train destination's corner note, or the
 * Journal curtain itself. A field that only existed on one of those surfaces
 * would hang `verify()` for the others.
 */
export function PassphraseChallengeSurface({
  broker,
}: PassphraseChallengeSurfaceProps): ReactNode {
  const challenge = useSyncExternalStore(
    useCallback((listener: () => void) => broker.subscribe(listener), [broker]),
    useCallback(() => broker.current(), [broker]),
    useCallback(() => broker.current(), [broker]),
  );

  return challenge === null ? null : (
    <PassphraseChallengeDialog challenge={challenge} />
  );
}

/**
 * Split from the surface so the field's state is **keyed to one challenge**.
 * Keeping the entry in the subscribing component would carry a half-typed
 * passphrase from a cancelled prompt into the next one, and the next prompt may
 * be authorizing something else entirely.
 */
function PassphraseChallengeDialog({
  challenge,
}: {
  readonly challenge: PassphraseChallenge;
}): ReactNode {
  const [entered, setEntered] = useState("");
  const field = useRef<HTMLInputElement | null>(null);
  const fieldId = useId();

  return (
    <Sheet
      open
      centered
      // The prompt is a decision the user must answer before the thing that
      // raised it can continue, which is what `alertdialog` is for.
      role="alertdialog"
      title={PASSPHRASE_CHALLENGE_COPY.title}
      description={JOURNAL_AUTH_REASONS[challenge.reason]}
      // A stray click outside must not answer an authorization prompt.
      dismissOnScrimClick={false}
      onDismiss={() => {
        challenge.cancel();
      }}
      initialFocus={field}
      testId={PASSPHRASE_CHALLENGE_TEST_IDS.surface}
      footer={
        <>
          <Button
            variant="primary"
            fullWidth={false}
            onPress={() => {
              challenge.submit(entered);
            }}
            testId={PASSPHRASE_CHALLENGE_TEST_IDS.submit}
          >
            {JOURNAL_PRIVACY_COPY.unlock}
          </Button>
          <Button
            variant="secondary"
            fullWidth={false}
            onPress={() => {
              challenge.cancel();
            }}
            testId={PASSPHRASE_CHALLENGE_TEST_IDS.cancel}
          >
            {JOURNAL_PRIVACY_COPY.notNow}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          // Enter in the field submits, which is how a password prompt behaves
          // everywhere else; without this the key press does nothing and the
          // user retypes into a field that already holds the answer.
          event.preventDefault();
          challenge.submit(entered);
        }}
      >
        <label htmlFor={fieldId}>
          <Text as="span" variant="footnote" tone="secondary">
            {PASSPHRASE_CHALLENGE_COPY.field}
          </Text>
        </label>
        <input
          id={fieldId}
          ref={field}
          type="password"
          // Never offered to a password manager: this passphrase derives the key
          // for local journal content and is not an account credential.
          autoComplete="off"
          value={entered}
          data-testid={PASSPHRASE_CHALLENGE_TEST_IDS.field}
          onChange={(event) => {
            setEntered(event.target.value);
          }}
        />
      </form>
      <Text variant="footnote" tone="secondary">
        {PASSPHRASE_CHALLENGE_COPY.hint}
      </Text>
    </Sheet>
  );
}
